import React, { useState } from "react";
import CorrectGreen from "src/img/airwallex/PrimaryMethod.svg";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import {
    LottieLoadingIndicator,
    Toast,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalState,
    StandardModalText
} from "src/components";

function AccountDataRequest({ token, open, handleClose, exportType = "employee" }) {
    const [isSuccess, setIsSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        setLoading(true);
        
        try {
            const endpoint = exportType === "account_owner"
                ? `${API_URL}/company/exportDataRequest`
                : `${API_URL}/company/employee/exportDataRequest`;
 
            const response = await axios.post(endpoint, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data?.status === "pending") {
                Toast.info("You already requested a data export. Please wait until it is completed. Check your email for updates.");
                return;
            }
            await new Promise(resolve => setTimeout(resolve, 500));
            setIsSuccess(true);
        } catch (err) {
            setIsSuccess(false);
            console.error(err);
            Toast.error("Failed to request data export. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDialogClose = () => {
        setIsSuccess(false);
        handleClose();
    };

    const isCompanyExport = exportType === "account_owner";
    const title = isCompanyExport
        ? "Confirm Company Data Export Request"
        : "Confirm Data Export Request";

    const successTitle = "Data Export Request Submitted";
    const activeTitle = isSuccess ? successTitle : title;
    const footer = loading ? null : isSuccess ? (
        <StandardModalActionGroup $center justifyContent="center">
            <StandardModalActionButton onClick={handleDialogClose}>
                Close
            </StandardModalActionButton>
        </StandardModalActionGroup>
    ) : (
        <StandardModalActionGroup $pushRight>
            <StandardModalActionButton onClick={handleDialogClose}>
                Cancel
            </StandardModalActionButton>
            <StandardModalActionButton $variant="primary" onClick={handleConfirm}>
                Confirm request
            </StandardModalActionButton>
        </StandardModalActionGroup>
    );

    const successBody = isCompanyExport
        ? (
            <>
                <StandardModalText>
                    Your request to download your <strong>company and personal data</strong> has been received.
                </StandardModalText>
                <StandardModalText>
                    We are now preparing your export file. This process can take up to 72 hours, depending on data volume.
                </StandardModalText>
                <StandardModalText>
                    Once ready, you will receive an email with a secure download link valid for 48 hours. If you do not receive your file within 3 business days, please contact support@mebusiness.com.au.
                </StandardModalText>
                <StandardModalText>
                    <strong>Please note:</strong> Personal employee data and encrypted chat messages are excluded from company exports.
                </StandardModalText>
            </>
        )
        : (
            <>
                <StandardModalText>
                    Your request to download your <strong>personal data</strong> has been received.
                </StandardModalText>
                <StandardModalText>
                    We are now preparing your export file. This process can take up to 72 hours, depending on data volume.
                </StandardModalText>
                <StandardModalText>
                    Once ready, you will receive an email with a secure download link valid for 48 hours. If you do not receive your file within 3 business days, please contact support@mebusiness.com.au.
                </StandardModalText>
                <StandardModalText>
                    <strong>Please note:</strong> Chat messages are end-to-end encrypted and cannot be exported. Business-level data belonging to your organisation will also be excluded.
                </StandardModalText>
            </>
        );

    const description = isCompanyExport
        ? (
            <>
                <StandardModalText>
                    You are requesting a download of your <strong>company-level data and personal data</strong> from Me Business.
                </StandardModalText>
                <StandardModalText>The following information will be included in your export:</StandardModalText>
                <StandardModalText as="ul" margin="0 0 0 1.15rem">
                    <li>Company details (name, ABN, contact info, etc.)</li>
                    <li>Company posts, polls, and surveys</li>
                    <li>Notices and announcements</li>
                    <li>Reactions, comments, and aggregated analytics</li>
                </StandardModalText>
                <StandardModalText>
                    A CSV file will be generated and emailed to your registered address once the request has been processed.
                </StandardModalText>
                <StandardModalText>
                    <strong>Please note:</strong> Individual employee data and encrypted chat messages will not be included.
                </StandardModalText>
            </>
        )
        : (
            <>
                <StandardModalText>
                    You are requesting a download of your <strong>personal data</strong> from Me Business.
                </StandardModalText>
                <StandardModalText>The following information will be included in your export:</StandardModalText>
                <StandardModalText as="ul" margin="0 0 0 1.15rem">
                    <li>Account details (name, email, role, etc.)</li>
                    <li>Profile and contact information</li>
                    <li>Your posts, goals, surveys, polls, and checklists created by you</li>
                    <li>Engagement summaries (aggregated usage statistics related to your activity)</li>
                </StandardModalText>
                <StandardModalText>
                    A CSV file will be generated and emailed to your registered address once the request has been processed.
                </StandardModalText>
                <StandardModalText>
                    <strong>Please note:</strong> Sensitive or private business data belonging to your organisation will not be included.
                </StandardModalText>
            </>
        );

    return (
        <StandardModal
            open={open}
            onClose={handleDialogClose}
            title={activeTitle}
            widthMode="large"
            footer={footer}
        >
            {loading ? (
                <StandardModalState minHeight="14rem">
                    <LottieLoadingIndicator height="4em" width="4em" />
                </StandardModalState>
            ) : isSuccess ? (
                <StandardModalSection gap="0.85rem">
                    <StandardModalText weight="700" size="1rem" color="#171A22">
                        <img src={CorrectGreen} alt="success" width="48px" height="48px" />
                    </StandardModalText>
                    <StandardModalText weight="700" color="#171A22">
                        {isCompanyExport ? "Your company export request has been received." : "Your personal data export request has been received."}
                    </StandardModalText>
                    {successBody}
                </StandardModalSection>
            ) : (
                <StandardModalSection gap="0.85rem">
                    {description}
                </StandardModalSection>
            )}
        </StandardModal>
    );
}

export default AccountDataRequest;
